import React, { useCallback, useMemo } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton
} from "@material-ui/core";
import {
  SaveAlt as SaveAltIcon,
  OpenInNew as OpenInNewIcon
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import {
  ActiveStatus,
  CollapsiblePanel,
  SubHeader
} from "src/apps/commons/dataDisplays";
import { download } from "src/apps/commons/fileUploads/api";
import {
  SimpleSubTable,
  SimpleColumnType,
  SimpleSubTablePlaceholder
} from "src/apps/commons/form";
import { ListModalState } from "src/apps/commons/form/hooks";
import { downloadFile } from "src/utils";
import { IRemyndManualSimple } from "../../remyndManual/types";
import { IRemyndProduct } from "../../remyndProduct/types";
import { IRemyndVerifiedUserProductManualSaveReq } from "../types";
import { RemyndManualListState } from "./Edit";

interface Props {
  remyndProduct: IDropdown<IRemyndProduct> | null;
  remyndManualListState: RemyndManualListState;
  savedManualList: IRemyndVerifiedUserProductManualSaveReq[];
  delItem: (index: number) => void;
  setModal: (
    state: ListModalState<IRemyndVerifiedUserProductManualSaveReq>
  ) => void;
  setManualNotify: (notify: boolean) => void;
  manualNotify: boolean;
  disabledMessage?: string;
}

type RowItemType =
  | IRemyndManualSimple
  | IRemyndVerifiedUserProductManualSaveReq;

const UserProductManual: React.FC<Props> = props => {
  const {
    remyndProduct,
    remyndManualListState,
    savedManualList,
    delItem,
    setModal,
    setManualNotify,
    manualNotify,
    disabledMessage
  } = props;

  const downloadAndSave = async (fileId: string) => {
    const resp = await download(fileId);
    await downloadFile(resp);
  };
  const DownloadCell = useCallback((data: RowItemType) => {
    const { fileId } = data;
    return (
      <IconButton
        aria-label="Download"
        onClick={async () => {
          if (fileId) await downloadAndSave(fileId);
        }}
        disabled={!fileId}
        size="small"
      >
        <SaveAltIcon />
      </IconButton>
    );
  }, []);

  const LinkCell = useCallback((data: RowItemType) => {
    const { link } = data;
    return link ? (
      <IconButton
        aria-label="Link"
        size="small"
        href={link}
        target="_blank"
        rel="noreferrer"
      >
        <OpenInNewIcon />
      </IconButton>
    ) : (
      <IconButton aria-label="Link" disabled size="small">
        <OpenInNewIcon />
      </IconButton>
    );
  }, []);

  const ActiveCell = useCallback(
    (data: RowItemType) => <ActiveStatus value={data.active} />,
    []
  );

  const columnDefs = useMemo<SimpleColumnType<RowItemType>[]>(
    () => [
      {
        label: "ชื่อ (TH)",
        minWidth: 180,
        renderCell: req => req.nameTh
      },
      {
        label: "ชื่อ (EN)",
        minWidth: 180,
        renderCell: req => req.nameEn
      },
      {
        label: "รายละเอียด (TH)",
        minWidth: 180,
        renderCell: req => req.descTh
      },
      {
        label: "รายละเอียด (EN)",
        minWidth: 180,
        renderCell: req => req.descEn
      },
      {
        label: "Link",
        align: "center",
        width: 80,
        style: { padding: 0 },
        renderCell: LinkCell
      },
      {
        label: "File",
        align: "center",
        width: 80,
        style: { padding: 0 },
        renderCell: DownloadCell
      }
    ],
    [LinkCell, DownloadCell]
  );

  return (
    <CollapsiblePanel title="Step 3. คู่มือ/ไฟล์แนบ" icon="edit">
      <Grid container spacing={2}>
        {disabledMessage && (
          <Grid item xs={12}>
            <Alert severity="error">{disabledMessage}</Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <SubHeader title="3.1. คู่มือ/ไฟล์แนบจาก Master" />
        </Grid>
        <Grid item xs={12}>
          {remyndManualListState.fetching ? (
            <SimpleSubTablePlaceholder loading />
          ) : remyndManualListState.list.length === 0 ? (
            <SimpleSubTablePlaceholder
              text={
                remyndProduct
                  ? `ไม่พบข้อมูล Master (ที่ Active) สำหรับสินค้าที่เลือก : ${remyndProduct.meta.nameTh}`
                  : `กรุณาเลือกสินค้า`
              }
            />
          ) : (
            <SimpleSubTable<IRemyndManualSimple>
              data={remyndManualListState.list}
              columns={columnDefs}
              showIndex
              readonly={!!disabledMessage}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <SubHeader
            title="3.2. คู่มือ/ไฟล์แนบ เฉพาะสำหรับ User Product นี้"
            marginTop
          />
        </Grid>
        <Grid item xs={12}>
          <SimpleSubTable<IRemyndVerifiedUserProductManualSaveReq>
            data={savedManualList}
            onClickAdd={() => setModal({ open: true })}
            onClickEdit={(req, idx) =>
              setModal({
                open: true,
                index: idx,
                item: req
              })
            }
            onClickDelete={(_, idx) => delItem(idx)}
            columns={[
              ...columnDefs,
              {
                label: "Active",
                align: "center",
                width: 80,
                renderCell: ActiveCell
              }
            ]}
            showIndex
            readonly={!!disabledMessage}
          />
        </Grid>
        {!disabledMessage && (
          <>
            <Grid item xs={12}>
              <Alert severity="success">
                เมื่อกดบันทึก ทาง user จะเห็นรายการคู่มือ/ไฟล์แนบ จากทั้งตาราง
                3.1. และ 3.2. รวมกัน (กรองเฉพาะที่ active)
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Box ml={1}>
                    <Checkbox
                      color="primary"
                      checked={manualNotify}
                      onChange={v => {
                        setManualNotify(v.target.checked);
                      }}
                    />
                  </Box>
                }
                label="ส่ง Notification แจ้ง User ด้วย (ระบบจะส่งเฉพาะถ้ามีการแก้ไขข้อมูล)"
                labelPlacement="end"
              />
            </Grid>
          </>
        )}
      </Grid>
    </CollapsiblePanel>
  );
};

export default UserProductManual;
